import Image from 'next/image'
import Link from 'next/link'
import { Mail, MapPin } from 'lucide-react'
import { StoreDownloadButtons } from '@/components/landing/StoreDownloadButtons'
import { MARKETING_APP_HREF } from '@/components/landing/marketingAppHref'

// Instagram Icon Component
const InstagramIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
)

// LinkedIn Icon Component
const LinkedInIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
)

interface FooterLink {
  name: string
  href: string
  comingSoon?: boolean
}

const footerLinks: {
  product: FooterLink[]
  useCases: FooterLink[]
  support: FooterLink[]
  company: FooterLink[]
} = {
  product: [
    { name: 'Home', href: '/#home' },
    { name: 'Play', href: '/#play' },
    { name: 'Parks', href: '/#parks' },
    { name: 'Picks', href: '/#picks' },
    { name: 'Travel', href: '/#travel' },
    { name: 'Download app', href: '/#download-app' },
    { name: 'Provider waitlist', href: '/#providers' },
    { name: 'How it works', href: '/how-it-works' },
    { name: 'Resources', href: '/resources' },
  ],
  useCases: [
    { name: 'All use cases', href: '/use-cases' },
    { name: 'Safe dog walking', href: '/use-cases/safe-dog-walking' },
    { name: 'Dog heat safety', href: '/use-cases/dog-heat-safety' },
    { name: 'Dog travel', href: '/use-cases/dog-travel' },
    { name: 'Dog socialization', href: '/use-cases/dog-socialization' },
  ],
  support: [
    { name: 'Help Center', href: '/help' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Community', href: '/community', comingSoon: true },
    { name: 'Status', href: '/status', comingSoon: true },
  ],
  company: [
    { name: 'About Us', href: '/about' },
    { name: 'Careers', href: '/careers', comingSoon: true },
    { name: 'Press', href: '/press', comingSoon: true },
    { name: 'Blog', href: '/blog', comingSoon: true },
  ],
}

const socialLinks = [
  {
    name: 'Instagram',
    href: 'https://instagram.com/wagamourapp',
    icon: InstagramIcon,
  },
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/wagamour',
    icon: LinkedInIcon,
  },
]

export function Footer() {
  return (
    <footer className="bg-[radial-gradient(circle_at_20%_0%,rgba(251,191,36,0.16),transparent_28%),linear-gradient(135deg,#0b0712_0%,#17101f_54%,#2a152a_100%)] text-white">
      <div className="container-mobile py-16">
        <div
          id="download-app"
          className="mb-12 rounded-[30px] border border-[#FFF6E8]/18 bg-[#FFF6E8]/[0.1] p-8 text-center shadow-[0_24px_80px_-34px_rgba(251,191,36,0.4)] backdrop-blur-xl scroll-mt-28 md:p-10"
        >
          <h3 className="font-heading text-xl font-bold text-white md:text-2xl">
            Download Wagamour today
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-[#FFF6E8]/80">
            Install the native iPhone or Android app, or open Wagamour instantly
            on the web.
          </p>
          <div className="mx-auto mt-6 max-w-3xl">
            <StoreDownloadButtons
              includePwa
              size="lg"
              webAppHref={MARKETING_APP_HREF}
            />
          </div>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-6">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link href="/" className="flex items-center space-x-2 mb-6">
              <Image
                src="/wagamour-logo-registered-white.png"
                width={496}
                height={127}
                alt="Wagamour®"
                className="h-12 w-auto"
                unoptimized
              />
            </Link>

            <p className="mb-6 leading-relaxed text-[#FFF6E8]/72">
              The Pet Mobility Platform — Pet Comfort Intelligence™
            </p>

            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 text-white/80">
                <Mail className="h-4 w-4 text-[#FDE68A]" />
                <a
                  href="mailto:hello@wagamour.com"
                  className="hover:text-white transition-colors"
                >
                  hello@wagamour.com
                </a>
              </div>
              <div className="flex items-center space-x-3 text-white/80">
                <MapPin className="h-4 w-4 text-[#FDE68A]" />
                <span>San Francisco, CA</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const IconComponent = social.icon
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#FFF6E8]/10 transition-colors hover:bg-[#F59E0B]"
                    aria-label={social.name}
                  >
                    {typeof IconComponent === 'function' ? (
                      <IconComponent className="w-5 h-5" />
                    ) : (
                      <span className="text-lg">{IconComponent}</span>
                    )}
                  </a>
                )
              })}
            </div>
          </div>

          {/* Links Sections */}
          <div>
            <h3 className="font-semibold text-white mb-4">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Use cases</h3>
            <ul className="space-y-3">
              {footerLinks.useCases.map((link) => (
                <li key={link.name}>
                  <Link
                    href={link.href}
                    className="text-white/80 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.comingSoon ? (
                    <span className="text-white/60 cursor-not-allowed flex items-center gap-2">
                      {link.name}
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    </span>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.comingSoon ? (
                    <span className="text-white/60 cursor-not-allowed flex items-center gap-2">
                      {link.name}
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                        Coming Soon
                      </span>
                    </span>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-white/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/70 text-sm">
              © 2026 Wagamour LLC. All rights reserved.
            </div>

            <div className="flex flex-wrap gap-4 justify-center md:justify-end">
              <Link
                href="/beta-disclaimer"
                className="text-white/70 hover:text-white text-sm transition-colors"
              >
                Product disclaimer
              </Link>
              <span className="text-white/50">•</span>
              <Link
                href="/privacy"
                className="text-white/70 hover:text-white text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="text-white/50">•</span>
              <Link
                href="/terms"
                className="text-white/70 hover:text-white text-sm transition-colors"
              >
                Terms of Service
              </Link>
              <span className="text-white/50">•</span>
              <Link
                href="/delete-account"
                className="text-white/70 hover:text-white text-sm transition-colors"
              >
                Delete account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
